import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  addImageToThePage,
  addNewSpread,
  deleteImage,
  deletePhotobook,
  deleteSpread,
  fetchLayouts,
  fetchPhotobook,
  fetchPhotobooks,
  fetchPreviewPhotobook,
  replaceImage,
  reuploadPreviousState,
  swapImage,
  updateImageBgPosition,
  updatePhotobookLayout,
  updatePhotobookTitle
} from './actions';
import { addCoverSpread } from 'modules/ScrPhotobook/helpers/addCoverSpread';
import { isString, uniqBy } from 'lodash';

export interface IBoundsRelative {
  heightPercent: number;
  widthPercent: number;
  leftPercent: number;
  topPercent: number;
}

export interface ISpreadImage {
  id: string;
  frameIndex: number;
  boundsRelative: IBoundsRelative;
}

export interface IPhotobookSpread {
  _id: string;
  index: number;
  frameLayout: null | string;
  images: ISpreadImage[];
  isCoverSlide?: boolean;
  cover?: string;
}

export interface IPhotobookPricing {
  price?: number;
  priceForAdditionalPages?: number;
  priceTotal?: number;
}

export interface IProductDetails {
  color?: string;
  coverTextPosition?: string;
  coverFirstTitle?: string;
  coverSecondTitle?: string;
  coverFirstTitleFont?: string;
  coverSecondTitleFont?: string;
  coverImage?: ISpreadImage;
}

export enum CoverTextPosition {
  BOTTOM_CENTER_HORIZONTAL = 'BOTTOM_CENTER_HORIZONTAL',
  BOTTOM_LEFT_HORIZONTAL = 'BOTTOM_LEFT_HORIZONTAL',
  BOTTOM_RIGHT_HORIZONTAL = 'BOTTOM_RIGHT_HORIZONTAL',
  CENTER_HORIZONTAL = 'CENTER_HORIZONTAL',
  RIGHT_CENTER_VERTICAL = 'RIGHT_CENTER_VERTICAL'
}

// Mapping from internal enum values to German API values
export const COVER_TEXT_POSITION_API_MAP = {
  [CoverTextPosition.BOTTOM_CENTER_HORIZONTAL]: 'Unten Mitte Horizontal',
  [CoverTextPosition.BOTTOM_LEFT_HORIZONTAL]: 'Unten Links Horizontal',
  [CoverTextPosition.BOTTOM_RIGHT_HORIZONTAL]: 'Unten Rechts Horizontal',
  [CoverTextPosition.CENTER_HORIZONTAL]: 'Mitte Horizontal',
  [CoverTextPosition.RIGHT_CENTER_VERTICAL]: 'Rechts Mitte Vertikal'
} as const;

// Reverse mapping from German API values to internal enum values
export const COVER_TEXT_POSITION_FROM_API_MAP = {
  'Unten Mitte Horizontal': CoverTextPosition.BOTTOM_CENTER_HORIZONTAL,
  'Unten Links Horizontal': CoverTextPosition.BOTTOM_LEFT_HORIZONTAL,
  'Unten Rechts Horizontal': CoverTextPosition.BOTTOM_RIGHT_HORIZONTAL,
  'Mitte Horizontal': CoverTextPosition.CENTER_HORIZONTAL,
  'Rechts Mitte Vertikal': CoverTextPosition.RIGHT_CENTER_VERTICAL
} as const;

// Helper function to convert API position to enum
export const convertApiPositionToEnum = (apiPosition?: string): CoverTextPosition => {
  if (!apiPosition) {
    return CoverTextPosition.BOTTOM_CENTER_HORIZONTAL;
  }

  const mappedPosition =
    COVER_TEXT_POSITION_FROM_API_MAP[apiPosition as keyof typeof COVER_TEXT_POSITION_FROM_API_MAP];

  return mappedPosition ?? CoverTextPosition.BOTTOM_CENTER_HORIZONTAL;
};

type PhotobookMode = 'crop' | 'swap' | '';
export type CoverPositions = CoverTextPosition;
export const coverPositionsArray: CoverPositions[] = [
  CoverTextPosition.BOTTOM_CENTER_HORIZONTAL,
  CoverTextPosition.BOTTOM_LEFT_HORIZONTAL,
  CoverTextPosition.BOTTOM_RIGHT_HORIZONTAL,
  CoverTextPosition.CENTER_HORIZONTAL,
  CoverTextPosition.RIGHT_CENTER_VERTICAL
];

export interface IPhotobook {
  _id: string;
  _user: string;
  _endcustomer: string;
  _product: string;
  _collection: string;

  title: string;
  slug: string;
  aspectRatio: number;

  productImage: string;
  details: {
    usedImages: string[];
    numberOfUsedImages: number;
    numberOfPages: number;
    numberOfSpreads: number;
  };
  productInformation: {
    size: number;
    color: string;
    minAmountOfPages: number;
    maxAmountOfPages: number;
  };
  productDetails: IProductDetails;

  spreads: IPhotobookSpread[];

  availableSelections: string[];
  authorizedEditors: string[];
  sharedViewers: string[];

  pricing: IPhotobookPricing;

  isSuggested: boolean;
  isDeleted: boolean;
  finalDeletionAt: Date;

  createdAt: string;
  updatedAt: string;
}

export interface ILayout {
  _id: string;
  numberOfFrames: number;
  frames: Array<{ boundsRelative: IBoundsRelative }>;
}

export interface IPhotobookState {
  photobooks: IPhotobook[];
  suggestedPhotobooks: IPhotobook[];
  purchasedPhotobooks: IPhotobook[];
  // Preview-only books (e.g. other customers' books being previewed in the cart). Kept separate
  // because fetchPhotobooks replaces `photobooks` with only the current user's own books.
  previewPhotobooks: IPhotobook[];
  layouts: ILayout[];
  activePageIdx: number | null;
  activatedImageId: string;
  isImageDragged?: boolean;
  isMobileCoverEdit?: boolean;
  mode: PhotobookMode;
  lastUpdate?: string;
  isUpdateProcessed?: boolean;
  activeCoverField?: string;
  startingFirstTitle?: string;
  startingSecondTitle?: string;
  coverDulpicateFirstTitle?: string;
  coverDulpicateSecondTitle?: string;
  requestHistory: IRequestHistory;
}

interface IRequestHistory {
  past: IPhotobookState[];
  present: IPhotobookState | null;
  future: IPhotobookState[];
}

export enum PhotobookUpdateType {
  ChangeTitle = 'change_title',
  AddImage = 'add_image',
  UpdateCoverImage = 'update_cover_image',
  ReplaceImage = 'replace_image',
  DeleteImage = 'delete_image',
  SwapImage = 'swap_image',
  UpdateImageCropping = 'update_image_cropping',
  AddSpread = 'add_spread',
  UpdateSpreadLayout = 'update_spread_layout',
  DeleteSpread = 'delete_spread',
  UpdateCover = 'update_cover',
  UpdateCoverText = 'update_cover_text',
  UpdateUndoRedoAction = 'update_undo_redo_action',
  ReuploadSpreads = 'reaupload_spreads'
}

const initialState: IPhotobookState = {
  photobooks: [],
  purchasedPhotobooks: [],
  suggestedPhotobooks: [],
  previewPhotobooks: [],
  layouts: [],
  activePageIdx: 0,
  activatedImageId: '',
  isImageDragged: false,
  isMobileCoverEdit: false,
  mode: '',
  lastUpdate: '',
  isUpdateProcessed: false,
  activeCoverField: '',
  startingFirstTitle: '',
  startingSecondTitle: '',
  coverDulpicateFirstTitle: '',
  coverDulpicateSecondTitle: '',
  requestHistory: {
    past: [],
    present: null,
    future: []
  }
};

const photobookSlice = createSlice({
  name: 'photobook',
  initialState,
  reducers: {
    setPhotobook(state, { payload }: PayloadAction<IPhotobookState | null>) {
      Object.assign(state, payload);
    },
    setActivePageIdx(state, { payload }: PayloadAction<number | null>) {
      const isPageChanged = state.activePageIdx !== payload;
      if (!isPageChanged) return;

      state.activePageIdx = payload;
      state.activatedImageId = '';
      state.mode = '';
    },
    setFirstPageAsCover(state, { payload }: PayloadAction<{ id: string }>) {
      const currentPhotobookIdx = state.photobooks.findIndex((item) => item._id === payload.id);

      state.photobooks[currentPhotobookIdx].spreads[0].isCoverSlide = true;
    },
    changePhotobookTitle(state, { payload }: PayloadAction<{ id: string; value: string }>) {
      const currentPhotobookIdx = state.photobooks.findIndex((item) => item._id === payload.id);

      state.photobooks[currentPhotobookIdx].title = payload.value;
    },
    updateSpreads(
      state,
      { payload }: PayloadAction<{ photobookId: string; spreads: IPhotobookSpread[] }>
    ) {
      const currentPhotobookIdx = state.photobooks.findIndex(
        (item) => item._id === payload.photobookId
      );

      const spreads = [...payload.spreads];

      const coverSpread = { _id: '', frameLayout: '', images: [], index: 0, isCoverSlide: true };

      spreads.unshift(coverSpread);

      const spreadsWithIndex = spreads.map((spread, index) => ({ ...spread, index }));

      state.photobooks[currentPhotobookIdx].spreads = spreadsWithIndex;
    },
    // updateCoverSpread(
    //   state,
    //   { payload }: PayloadAction<{ photobookId: string; coverSpread: IPhotobookSpread }>
    // ) {
    //   const currentPhotobookIdx = state.photobooks.findIndex(
    //     (item) => item._id === payload.photobookId
    //   );

    //   state.photobooks[currentPhotobookIdx].spreads[0] = payload.coverSpread;
    // },
    updatePricing(
      state,
      { payload }: PayloadAction<{ photobookId: string; pricing: IPhotobookPricing }>
    ) {
      const currentPhotobookIdx = state.photobooks.findIndex(
        (item) => item._id === payload.photobookId
      );

      state.photobooks[currentPhotobookIdx].pricing = payload.pricing;
    },
    setActiveImageId(state, { payload }: PayloadAction<{ activatedImageId: string }>) {
      state.activatedImageId = payload.activatedImageId;
      state.mode = '';
    },
    setCoverImage(
      state,
      { payload }: PayloadAction<{ photobookId: string; coverImage: ISpreadImage }>
    ) {
      const currentPhotobookIdx = state.photobooks.findIndex(
        (item) => item._id === payload.photobookId
      );

      state.photobooks[currentPhotobookIdx].productDetails.coverImage = payload.coverImage;
    },
    startImageDrag(state) {
      state.isImageDragged = true;
    },
    endImageDrag(state) {
      state.isImageDragged = false;
    },
    setPhotobookMode(state, { payload }: PayloadAction<PhotobookMode>) {
      const isSameModeSelected = state.mode === payload;
      state.mode = isSameModeSelected ? '' : payload;
    },
    setPhotobookCover(state, { payload }: PayloadAction<{ photobookId: string; cover?: string }>) {
      const currentPhotobookIdx = state.photobooks.findIndex(
        (item) => item._id === payload.photobookId
      );

      state.photobooks[currentPhotobookIdx].productDetails.color = payload.cover;
    },
    setPhotobookCoverText(
      state,
      {
        payload
      }: PayloadAction<{
        photobookId: string;
        coverTextPosition?: CoverPositions;
        coverFirstTitle?: string;
        coverSecondTitle?: string;
        coverFirstTitleFont?: string;
        coverSecondTitleFont?: string;
      }>
    ) {
      const currentPhotobookIdx = state.photobooks.findIndex(
        (item) => item._id === payload.photobookId
      );

      const currentDetails = state.photobooks[currentPhotobookIdx].productDetails;

      if (payload.coverTextPosition) {
        // Convert enum to German string for consistency with API
        currentDetails.coverTextPosition =
          COVER_TEXT_POSITION_API_MAP[payload.coverTextPosition] || payload.coverTextPosition;
      }

      if (isString(payload.coverFirstTitle)) {
        currentDetails.coverFirstTitle = payload?.coverFirstTitle || '';
      }
      if (isString(payload.coverSecondTitle)) {
        currentDetails.coverSecondTitle = payload?.coverSecondTitle || '';
      }

      if (payload.coverFirstTitleFont) {
        currentDetails.coverFirstTitleFont = payload.coverFirstTitleFont;
      }
      if (payload.coverSecondTitleFont) {
        currentDetails.coverSecondTitleFont = payload.coverSecondTitleFont;
      }
    },
    updatePhotobookDuplicateCoverText(
      state,
      { payload }: PayloadAction<{ coverFirstTitle?: string; coverSecondTitle?: string }>
    ) {
      if (isString(payload.coverFirstTitle)) {
        state.coverDulpicateFirstTitle = payload?.coverFirstTitle || '';
      }
      if (isString(payload.coverSecondTitle)) {
        state.coverDulpicateSecondTitle = payload?.coverSecondTitle || '';
      }
    },
    setStartingTitle(
      state,
      { payload }: PayloadAction<{ coverFirstTitle: string; coverSecondTitle: string }>
    ) {
      state.startingFirstTitle = payload.coverFirstTitle;
      state.startingSecondTitle = payload.coverSecondTitle;
    },
    setActiveField(state, { payload }: PayloadAction<string>) {
      state.activeCoverField = payload;
    },
    startMobileCoverEditing(state) {
      state.isMobileCoverEdit = true;
    },
    endMobileCoverEditing(state) {
      state.isMobileCoverEdit = false;
    },
    setLastUpdate(state, { payload }: PayloadAction<string>) {
      state.lastUpdate = payload;
    },
    saveRequest(state, { payload }: PayloadAction<{ action: string; payload: IPhotobookState }>) {
      // already went some steps back and made changes - means all the previous history is not valid anymore
      if (state.requestHistory.future.length) {
        state.requestHistory.past = [];
      }
      if (state.requestHistory.past.length >= 10) {
        state.requestHistory.past = state.requestHistory.past.slice(-10);
      }

      state.requestHistory.past.push(payload.payload);
      state.requestHistory.future = [];
    },
    undoRequest(state) {
      if (state.requestHistory.past.length > 0) {
        state.requestHistory.future.unshift(state);
        state.requestHistory.past.pop()!;
        state.lastUpdate = new Date().toISOString();
      }
    },
    redoRequest(state) {
      if (state.requestHistory.future.length > 0) {
        state.requestHistory.past.push(state);
        state.requestHistory.future.shift()!;
        state.lastUpdate = new Date().toISOString();
      }
    },
    clearRequestHistory(state) {
      state.requestHistory = {
        past: [],
        present: null,
        future: []
      };
    }
  },
  extraReducers: (builder) => {
    // @ts-ignore
    builder.addCase(
      fetchPhotobooks.fulfilled,
      (
        state,
        {
          payload
        }: PayloadAction<{
          availablePhotobooks: IPhotobook[];
          purchasedPhotobooks: IPhotobook[];
          suggestedPhotobooks: IPhotobook[];
        }>
      ) => {
        const { availablePhotobooks, purchasedPhotobooks, suggestedPhotobooks } = payload || {};
        const { photobooksWithCoverSpread } = addCoverSpread(availablePhotobooks);
        const { photobooksWithCoverSpread: suggestedPhotobooksWithCoverSpread } =
          addCoverSpread(suggestedPhotobooks);

        // Always use fresh data from fetch, no caching
        state.photobooks = photobooksWithCoverSpread;

        // Update purchasedPhotobooks with unique values
        state.purchasedPhotobooks = uniqBy(purchasedPhotobooks, '_id');
        state.suggestedPhotobooks = uniqBy(suggestedPhotobooksWithCoverSpread, '_id');
      }
    );
    // @ts-ignore
    builder.addCase(
      fetchPhotobook.fulfilled,
      (
        state,
        {
          payload
        }: PayloadAction<{
          photobook: IPhotobook;
        }>
      ) => {
        const { photobook } = payload;
        const { photobooksWithCoverSpread } = addCoverSpread([photobook]);

        state.photobooks = uniqBy([...state.photobooks, ...photobooksWithCoverSpread], '_id');
      }
    );
    builder.addCase(fetchPreviewPhotobook.fulfilled, (state, { payload }) => {
      if (!payload) return;

      const { photobooksWithCoverSpread } = addCoverSpread([payload]);

      // Store in a dedicated bucket so fetchPhotobooks (which replaces `photobooks` with the
      // current user's own books) can't evict the previewed book.
      state.previewPhotobooks = uniqBy(
        [...state.previewPhotobooks, ...photobooksWithCoverSpread],
        '_id'
      );
    });
    builder.addCase(fetchLayouts.fulfilled, (state, { payload }) => {
      state.layouts = payload;
    });
    builder.addCase(deletePhotobook.fulfilled, (state, { payload }) => {
      state.photobooks = state.photobooks.filter((photobook) => photobook._id !== payload);
    });

    // Processing actions
    builder.addCase(updatePhotobookTitle.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(updatePhotobookTitle.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(updatePhotobookLayout.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(updatePhotobookLayout.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(addNewSpread.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(addNewSpread.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(deleteSpread.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(deleteSpread.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(addImageToThePage.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(addImageToThePage.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(replaceImage.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(replaceImage.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(updateImageBgPosition.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(updateImageBgPosition.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(deleteImage.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(deleteImage.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(swapImage.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(swapImage.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });

    builder.addCase(reuploadPreviousState.pending, (state) => {
      state.isUpdateProcessed = true;
    });
    builder.addCase(reuploadPreviousState.fulfilled, (state) => {
      state.isUpdateProcessed = false;
    });
  }
});

export const {
  setPhotobook,
  setActivePageIdx,
  changePhotobookTitle,
  updateSpreads,
  updatePricing,
  setCoverImage,
  setActiveImageId,
  startImageDrag,
  endImageDrag,
  setPhotobookMode,
  setPhotobookCover,
  setPhotobookCoverText,
  setActiveField,
  startMobileCoverEditing,
  endMobileCoverEditing,
  setLastUpdate,
  updatePhotobookDuplicateCoverText,
  setStartingTitle,
  saveRequest,
  undoRequest,
  redoRequest,
  clearRequestHistory
} = photobookSlice.actions;

export const photobookReducer = photobookSlice.reducer;
